import { useMemo } from 'react';

type QueryParamsInput = Record<string, string | number | boolean | null | undefined>;

/**
 * Hook para construir query params de forma simples
 * @param params - Objeto com os parâmetros da query
 * @returns String com os query params formatados (ex: "?page=1&rowsPerPage=10")
 *
 * @example
 * const queryString = useQueryParams({ page: 1, rowsPerPage: 10, search: 'test' });
 * // Retorna: "?page=1&rowsPerPage=10&search=test"
 */
export function useQueryParams(params: QueryParamsInput): string {
  return useMemo(() => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      // Ignora valores null, undefined ou strings vazias
      if (value !== null && value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }, [params]);
}

/**
 * Função helper para construir query params (versão não-hook)
 * @param params - Objeto com os parâmetros da query
 * @returns String com os query params formatados (ex: "?page=1&rowsPerPage=10")
 *
 * @example
 * const queryString = buildQueryParams({ page: 1, rowsPerPage: 10, search: 'test' });
 * // Retorna: "?page=1&rowsPerPage=10&search=test"
 */
export function buildQueryParams(params: QueryParamsInput): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    // Ignora valores null, undefined ou strings vazias
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
